// allow: cache-buster -- module reload cache-bust query
const retryLimit = 3;
const retryDelayMilliseconds = 1500;
const retryDelayAddedMaximumJitterMilliseconds = 1000;

/** Error prefix for failed import retries */
export const IMPORT_RETRY_FAILED_ERROR = 'Failed to import';

export interface ImportInfo {
	path: string;
	exportName?: string;
}

export function extractImportInfo(importer: () => Promise<unknown>): ImportInfo {
	const src = importer.toString();

	const importMatch = /import\(["']([^"']+)["']\)/.exec(src);
	if (!importMatch) {
		throw new Error('Cannot extract import path');
	}

	const thenMatch = /\.then\(\s*\(?(\w+)\)?\s*=>\s*\1\.([A-Z]\w*)\)/.exec(src);

	return {
		path: importMatch[1],
		exportName: thenMatch?.[2],
	};
}

const timeoutWithJitter = (timeMs: number, maxJitterMs: number): Promise<void> =>
	new Promise(resolve => {
		const realTime = timeMs + Math.ceil(Math.random() * maxJitterMs);
		setTimeout(resolve, realTime);
	});

const importWithRetry = <T>(importPath: string, count: number): Promise<T> => {
	const retry = timeoutWithJitter(retryDelayMilliseconds, retryDelayAddedMaximumJitterMilliseconds).then(() => {
		const url = `${importPath}?${new Date().getTime()}`;
		return import(/* @vite-ignore */ url);
	});
	if (count < retryLimit) {
		return retry.catch(() => importWithRetry(importPath, count + 1));
	}
	return retry.catch(() => {
		throw new Error(`${IMPORT_RETRY_FAILED_ERROR} ${importPath} after ${retryLimit} retries`);
	});
};

export function recoverOnImportFailure<RESULT>(importer: () => Promise<RESULT>): () => Promise<RESULT> {
	let pendingOrSuccess: null | Promise<RESULT> = null;

	const importInfo = extractImportInfo(importer);

	const importModule = (): Promise<Record<string, RESULT>> =>
		importWithRetry<Record<string, RESULT>>(importInfo.path, 1);

	const resolveResult = (module: Record<string, RESULT>): RESULT => {
		if (!importInfo.exportName) {
			return module as RESULT;
		}
		return module[importInfo.exportName];
	};

	return (): Promise<RESULT> => {
		if (!pendingOrSuccess) {
			pendingOrSuccess = importer().catch(() =>
				importModule()
					.then(resolveResult)
					.catch(error => {
						pendingOrSuccess = null;
						throw error;
					}),
			);
		}

		return pendingOrSuccess;
	};
}
